import {
  useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { data as fmtData, faixaProfundidade, numero, plural, rotulo } from '../formato';
import {
  Aviso, Carregando, ErroDaApi, LinkProtegido, Selo, Vazio, type Tom,
  SeloDoEnsaio,
} from '../ui';
import {
  GRUPOS_DE_SIMBOLO, NOME_DO_SIMBOLO, TIPOS_DE_SIMBOLO, clienteResumido,
  desenhoDoSimbolo, estadoDoLaboratorio, estadoDoPonto, simboloDaAmostra,
  simboloDaPrevista, simboloDoPonto, SO_LABORATORIO, type EstadoSimbolo, type TipoSimbolo,
} from '../simbolosDoMapa';
import {
  ObraDoAcervo, PainelDoMapa, simbolosDoAcervo, type EscolherNoMapa, type RespostaDoAcervo,
} from './AcervoNoMapa';
// Só TIPOS do motor: o MapLibre é carregado sob demanda, no efeito do mapa
// (spec 05 §5.1) — uma importação comum o poria no pacote de todas as telas.
import type { ControleDoMapa, Falha, Feicao } from '../mapaGeotecnico';
import { aoMudarTemaEfetivo, temaEfetivo } from '../tema';
import { NomeDaAmostra, daApi, tituloDaAmostra } from '../NomeDaAmostra';

/**
 * O mapa dos ensaios — um item próprio do menu (onda 177; nasceu na 171).
 *
 * ── O pedido ────────────────────────────────────────────────────────
 *
 *   «Crie um mapa na parte de relatórios onde é possível consultar de
 *    forma interativa por cliente, por OS, por data, etc. todos os
 *    ensaios executados (com coordenada da coleta ou coordenada do
 *    ensaio executado — SPT, CPTu, Rotativa, Mista). Assim é possível
 *    acessar facilmente os relatórios por localização.»
 *                                              — Testesolo, 08/09/2026
 *
 * E, horas depois, o lugar dele e a crítica aos filtros:
 *
 *   «O mapa interativo de ensaios deverá ser um item à parte de
 *    relatórios. Ele pode servir mais como uma base de consulta caso um
 *    cliente não encontre uma campanha geotécnica específica. […] Os
 *    filtros ficaram muito toscos, sem busca por escrito, apenas
 *    dropdown. O layout de organização dos filtros ficou muito ruim
 *    também.»                                    — Testesolo, 08/09/2026
 *
 * ── A pergunta que ela responde ─────────────────────────────────────
 *
 * «O que já se ensaiou por AQUI?» — é uma base de consulta. O caso de
 * uso é o telefone tocando: um cliente não acha uma campanha, diz o
 * nome da obra, ou a rua, ou a cidade, e a pessoa do laboratório precisa
 * achar o laudo. Por isso a tela começa por UMA caixa de busca, e não
 * por nove listas suspensas.
 *
 * ── Como a busca funciona ───────────────────────────────────────────
 *
 * A pessoa escreve; a tela sugere, agrupado, o que existe com aquele
 * texto — clientes, OS, obras (pelo nome, pelo endereço, pela cidade),
 * cidades, métodos e ensaios de campo. Escolher uma sugestão vira uma
 * FICHA presa acima do mapa («Cliente: Prefeitura», «OS 2026-041»,
 * «Cidade: Blumenau/SC»); Enter sem escolher vira a ficha «contém
 * “texto”», que procura em tudo (OS, obra, endereço, cidade, cliente,
 * amostra, furo, método, número do laudo). Cada ficha tem o seu ×.
 *
 * As sugestões vêm do universo INTEIRO (a resposta traz `opcoes` sem
 * filtro), não do que já está na tela — senão ninguém troca de cliente.
 * Elas são filtradas aqui, sem ir ao servidor a cada tecla: o universo
 * cabe na memória e a resposta é imediata.
 *
 * O resto — período, situação, «só com laudo» — fica numa linha
 * discreta abaixo da busca, porque é o que se ajusta DEPOIS de achar o
 * lugar, não antes. No celular essa linha se recolhe atrás de «Ajustar».
 *
 * Os filtros vivem na URL (`/mapa?cliente=…&os=…`): o endereço que se
 * copia da barra é o mapa já filtrado, e voltar com o botão do navegador
 * volta o filtro.
 *
 * ── Três tons de pino ───────────────────────────────────────────────
 *
 * A coordenada vem de três lugares (`0171`): da AMOSTRA, do PONTO DE
 * INVESTIGAÇÃO ou, na falta dos dois, da OBRA. Os dois primeiros são
 * «aqui»; o terceiro é «por aqui». O pino de obra é vazado de propósito
 * — desenhar uma coordenada de reserva com o mesmo peso de um GPS seria
 * apresentar estimativa como levantamento, que é o que a `0025` já
 * recusava para a origem da coordenada.
 *
 * ── Este mapa e o do portal ─────────────────────────────────────────
 *
 * O portal do cliente (`0173`, `vw_portal_mapa`) também desenha um
 * mapa — o de FORA, que só mostra o que já foi entregue. Este é o de
 * DENTRO: todo ensaio e todo furo, em qualquer situação, com o caminho
 * até o laudo. São duas perguntas, e a segunda inclui a primeira.
 *
 * ── Por que MapLibre (onda 296, spec 05) ────────────────────────────
 *
 *   «Tem outro mapa melhor que o Leaflet? Que seja gratuito.»
 *                                              — Humberto, 06/10/2026
 *
 * Até a onda 296 o mapa era o Leaflet, que desenha cada símbolo como um
 * elemento da página: serve a alguns milhares de registros, não à visão de
 * todos os pontos, nem a relevo e tema escuro de verdade. O motor agora é
 * o MapLibre GL JS — aberto, sem chave e sem conta, como o §20 pede —, que
 * desenha em WebGL, com o fundo de ruas servido do NOSSO servidor (o
 * recorte do Brasil do Protomaps, em `/mapas/`) e nada de CDN (RN-MG3).
 *
 * A tela não fala com o MapLibre: usa o motor compartilhado com o Portal
 * (`mapaGeotecnico/`, idêntico byte a byte nos dois sites — RN-MG1), que
 * só é carregado quando o mapa abre. A busca, os filtros, o painel do
 * lugar e o acervo continuam aqui. Se o fundo não carregar, os símbolos
 * continuam onde estão e a tela diz isso; sem WebGL, ela avisa e mostra os
 * registros numa lista, com o caminho até o laudo (spec 05 §5.7).
 *
 * ── Os fundos (onda 179; o terceiro, na 296) ────────────────────────
 *
 *   «Coloque para escolher o tipo de mapa no canto dele (visão mapa ou
 *    visão earth).»                                — Testesolo, 10/09/2026
 *
 * No canto de cima, à direita, um seletor de fundos: **Mapa** (ruas,
 * nomes, números), **Satélite** (a imagem de satélite da Esri, «World
 * Imagery», que a Esri serve sem chave e sem conta, só com o crédito no
 * rodapé — é o que o §20 tolera; o Google Earth de verdade exigiria chave,
 * cota e contrato) e, desde a onda 296, **Satélite com ruas**. O seletor é
 * do motor; a escolha fica guardada no aparelho (`localStorage`, conforto
 * de quem usa; se o navegador recusar, volta ao Mapa), na mesma chave de
 * antes. O tema escuro tem fundo escuro próprio; o satélite não muda.
 *
 * ── Os símbolos, o agrupamento e a tela cheia (onda 215) ────────────
 *
 *   «Mapa de ensaios deverá ficar junto com Painel no menu lateral. […]
 *    aproveite a largura total da página e crie o modo full screen.
 *    Identifique com símbolos diferentes cada tipo de sondagem, ensaio
 *    de campo, ou apenas ensaio de laboratório.»
 *   «Ao concentrar muitos símbolos próximos crie uma forma de não ficar
 *    ruim de ler. Talvez criar círculos com a quantidade de ensaios e ao
 *    clicar abre ao redor, e ao dar mais zoom se separam.»
 *                                              — Humberto, 28/09/2026
 *
 * O pino deixa de ser «um disco por coordenada, com a contagem» e passa a
 * ser **um símbolo por registro**: um por furo/ponto, um por amostra
 * recebida (com os ensaios dela), um por amostra prevista. O desenho diz
 * o tipo (`simbolosDoMapa.ts`, a família aprovada em 28/09); vazado é
 * programada, cheio é executada; meia opacidade é a coordenada da obra.
 *
 * Registros próximos se juntam num CÍRCULO COM O NÚMERO (desde a onda
 * 296, o agrupamento nativo do motor): o zoom os separa; no mesmo ponto
 * exato, o clique os abre ao redor. Passar o mouse diz o nome
 * (a nomenclatura do cliente) e o cliente resumido; clicar abre ao lado
 * o lugar inteiro, com o laudo em aba nova — o painel de sempre.
 *
 * O filtro por tipo é a própria legenda: os mesmos símbolos, com a
 * contagem, e vive na URL (`tipos=sp,cptu`) como os outros. É filtro da
 * TELA, sobre o que a API já trouxe — trocar de tipo não vai ao
 * servidor.
 */

/*
 * ── O acervo antes do LabManager (onda 228) ─────────────────────────
 *
 *   «Talvez seja interessante que esses pontos apareçam no mapa com
 *    outra cor, indicando que foram obtidos antes da criação do
 *    LabManager. Crie um mini dashboard no topo também […].»
 *                                              — Humberto, 30/09/2026
 *
 * As obras de 2004 em diante (`0224`, RN-209) entram como mais uma
 * família de símbolo, `fonte = acervo`: o mesmo desenho por tipo, em
 * AZUL, e meia opacidade quando a posição veio do endereço. O acervo
 * obedece à busca por escrito, ao período e à cidade; os filtros que só
 * existem no LabManager (cliente do cadastro, OS, método, situação, laudo)
 * o deixam de fora — e a tela diz. Some com `acervo=0` na URL. O mini
 * painel do topo soma as duas fontes e diz quanto veio de cada uma.
 * Nada disso vai ao Portal do cliente: o acervo junta obras de todos.
 */

interface Linha {
  /** 0190: `prevista` é a amostra que ainda não chegou, com coordenada própria. */
  origem: 'ensaio' | 'ponto' | 'prevista';
  id: string;
  os_id: string;
  os_numero: string;
  os_status: string;
  obra_id: string;
  obra_codigo: string;
  obra_nome: string;
  endereco: string | null;
  municipio: string | null;
  uf: string | null;
  cliente_id: string;
  cliente: string;
  amostra_id: string | null;
  amostra_codigo: string | null;
  amostra_identificacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  data_coleta: string | null;
  ponto_id: string | null;
  ponto_codigo: string | null;
  ponto_tipo: string | null;
  ponto_situacao: string | null;
  metodo_codigo: string | null;
  metodo_nome: string | null;
  metodo_classe: string | null;
  status: string;
  data_execucao: string | null;
  data_aprovacao: string | null;
  data: string | null;
  laudo_numero: string | null;
  laudo_revisao: number | null;
  laudo_status: string | null;
  latitude: number | null;
  longitude: number | null;
  fonte_coordenada: 'amostra' | 'ponto' | 'obra' | 'prevista' | null;
  origem_coordenada: string | null;
  profundidade_prevista: string | null;
  profundidade_executada: string | null;
  tem_coordenada: boolean;
  coordenada_exata: boolean;
  /* 215: quem coletou, como veio e o tipo — o símbolo sai daqui. */
  amostra_origem: string | null;
  tipo_acondicionamento: string | null;
  tipo_amostra: string | null;
}

interface Resposta {
  linhas: Linha[];
  total: number;
  semCoordenada: number;
  opcoes: {
    clientes: { id: string; nome: string }[];
    os: { numero: string; obra_nome: string; cliente: string; linhas: number }[];
    obras: { id: string; codigo: string; nome: string; cliente: string;
      endereco: string | null; municipio: string | null; uf: string | null; linhas: number }[];
    metodos: { codigo: string; nome: string; ensaios: number }[];
    tiposDePonto: { tipo: string; pontos: number }[];
    cidades: { chave: string; municipio: string; uf: string | null; obras: number }[];
  };
}

/** Um lugar no mapa: a coordenada e tudo o que aconteceu nela. */
interface Lugar {
  chave: string;
  latitude: number;
  longitude: number;
  exata: boolean;
  fonte: 'amostra' | 'ponto' | 'obra' | 'prevista';
  linhas: Linha[];
}

const TIPO_PONTO: Record<string, string> = {
  spt: 'SPT', trado: 'Trado', rotativa: 'Rotativa', mista: 'Mista',
  cpt: 'CPT', cptu: 'CPTu', scptu: 'SCPTu', dmt: 'DMT', sdmt: 'SDMT',
  vane: 'Vane', permeabilidade: 'Permeabilidade', placa: 'Placa',
  poco: 'Poço', instrumento: 'Instrumento', outro: 'Outro',
};

const TOM_STATUS: Record<string, Tom> = {
  aprovado: 'ok', executado: 'ok',
  em_execucao: 'andamento', em_processamento: 'andamento',
  aguardando_verificacao: 'andamento', dados_completos: 'andamento',
  devolvido_para_correcao: 'atencao', pausado: 'atencao', suspenso: 'atencao',
  cancelado: 'critico',
};

const FONTE: Record<string, string> = {
  amostra: 'coordenada da coleta',
  ponto: 'coordenada do ponto de investigação',
  obra: 'coordenada da obra — localização aproximada',
  prevista: 'coordenada prevista — a amostra ainda não foi coletada',
};

/**
 * O que distingue um lugar do vizinho, numa linha: o furo (com o tipo),
 * a amostra, ou «obra» quando o pino é o da obra. Quatro pinos com o
 * mesmo nome de obra não deixam ninguém escolher.
 */
function resumoDoLugar(l: Lugar): string {
  const partes = new Set<string>();
  for (const x of l.linhas) {
    if (x.origem === 'ponto') {
      partes.add(`${x.ponto_codigo} (${TIPO_PONTO[x.ponto_tipo ?? ''] ?? x.ponto_tipo})`);
    } else if (x.origem === 'prevista') {
      partes.add(`${x.amostra_identificacao ?? 'amostra'} (prevista)`);
    } else if (x.fonte_coordenada === 'ponto' && x.ponto_codigo) {
      // 303 (RN-248): a amostra é dita pelo nome que o cliente deu.
      partes.add(`${x.ponto_codigo} · ${tituloDaAmostra(daApi(x))}`);
    } else if (x.fonte_coordenada === 'amostra') {
      partes.add(tituloDaAmostra(daApi(x)));
    }
  }
  if (!partes.size) return `obra ${l.linhas[0].obra_codigo} — aproximado`;
  const lista = [...partes];
  return lista.length > 3
    ? `${lista.slice(0, 3).join(', ')} e mais ${lista.length - 3}` : lista.join(', ');
}

/** Agrupa as linhas por coordenada — seis casas são ~10 cm. */
function agrupar(linhas: Linha[]): Lugar[] {
  const mapa = new Map<string, Lugar>();
  for (const l of linhas) {
    if (!l.tem_coordenada || l.latitude == null || l.longitude == null) continue;
    const chave = `${l.latitude.toFixed(6)},${l.longitude.toFixed(6)}`;
    let lugar = mapa.get(chave);
    if (!lugar) {
      lugar = {
        chave, latitude: l.latitude, longitude: l.longitude,
        exata: l.coordenada_exata, fonte: l.fonte_coordenada ?? 'obra', linhas: [],
      };
      mapa.set(chave, lugar);
    }
    lugar.linhas.push(l);
    /* Se qualquer linha do lugar tem coordenada exata, o lugar é exato. */
    if (l.coordenada_exata && !lugar.exata) {
      lugar.exata = true; lugar.fonte = l.fonte_coordenada ?? lugar.fonte;
    }
  }
  return [...mapa.values()];
}

/** A chave do lugar — a mesma coordenada de seis casas do `agrupar`. */
const chaveDoLugar = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

/**
 * Um símbolo no mapa: um furo, uma amostra recebida (com os ensaios
 * dela) ou uma amostra prevista. `lugar` é a coordenada, que é o que o
 * painel ao lado abre.
 */
interface Marcador {
  chave: string;
  lugar: string;
  tipo: TipoSimbolo;
  estado: EstadoSimbolo;
  /** 228: `acervo` é o que veio das pastas antigas — outra cor. */
  fonte: Lugar['fonte'] | 'acervo';
  estimada: boolean;
  nome: string;
  cliente: string;
  latitude: number;
  longitude: number;
  linhas: Linha[];
}

function marcadoresDe(linhas: Linha[]): Marcador[] {
  const todos = new Map<string, Marcador>();
  for (const l of linhas) {
    if (!l.tem_coordenada || l.latitude == null || l.longitude == null) continue;
    let chave: string; let tipo: TipoSimbolo; let nome: string;
    if (l.origem === 'ponto') {
      chave = `p:${l.id}`; tipo = simboloDoPonto(l.ponto_tipo);
      nome = l.ponto_codigo ?? 'ponto';
    } else if (l.origem === 'prevista') {
      chave = `v:${l.id}`; tipo = simboloDaPrevista(l.tipo_amostra);
      nome = l.amostra_identificacao ?? 'amostra prevista';
    } else {
      chave = `a:${l.amostra_id ?? l.id}`;
      tipo = simboloDaAmostra({ origem: l.amostra_origem,
        acondicionamento: l.tipo_acondicionamento, tipoAmostra: l.tipo_amostra });
      nome = l.amostra_identificacao ?? l.amostra_codigo ?? 'amostra';
    }
    let m = todos.get(chave);
    if (!m) {
      m = {
        chave, lugar: chaveDoLugar(l.latitude, l.longitude), tipo, estado: 'prog',
        fonte: l.fonte_coordenada ?? 'obra', estimada: l.fonte_coordenada === 'obra',
        nome, cliente: l.cliente, latitude: l.latitude, longitude: l.longitude, linhas: [],
      };
      todos.set(chave, m);
    }
    m.linhas.push(l);
  }
  for (const m of todos.values()) {
    const l = m.linhas[0];
    if (l.origem === 'ponto') m.estado = estadoDoPonto(l.ponto_situacao ?? l.status);
    else if (l.origem === 'prevista') m.estado = 'prog';
    else if (SO_LABORATORIO.includes(m.tipo)) m.estado = estadoDoLaboratorio(m.linhas.map((x) => x.status));
    else m.estado = 'exec';
  }
  return [...todos.values()];
}

const escapar = (t: string) => t.replace(/[&<>"']/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * A etiqueta de passar o mouse: o nome que o cliente usa, e o cliente. É
 * HTML, e o nome é do cliente: tudo escapado aqui, antes de chegar ao motor.
 */
const dicaDo = (m: Pick<Marcador, 'nome' | 'cliente' | 'tipo'>) =>
  `<b>${escapar(m.nome)}</b><br><small>${escapar(clienteResumido(m.cliente))}`
  + ` · ${escapar(NOME_DO_SIMBOLO[m.tipo])}</small>`;

/**
 * O mesmo, em texto, para a lista que o leitor de tela lê (spec 05 §4.6):
 * nome · tipo (programada | executada) · cliente resumido. Texto puro — o
 * motor o põe com `textContent`.
 */
const textoDaLista = (m: Pick<Marcador, 'nome' | 'cliente' | 'tipo' | 'estado'>) =>
  `${m.nome} · ${NOME_DO_SIMBOLO[m.tipo]} (${m.estado === 'exec' ? 'executada' : 'programada'})`
  + ` · ${clienteResumido(m.cliente)}`;

/** O filtro por tipo, na URL: `tipos=sp,cptu`. Vazio é «todos». */
function lerTipos(p: URLSearchParams): Set<TipoSimbolo> {
  const v = p.get('tipos');
  if (!v) return new Set();
  return new Set(v.split(',').filter((t): t is TipoSimbolo =>
    (TIPOS_DE_SIMBOLO as string[]).includes(t)));
}

/* A escolha do fundo fica guardada no aparelho, nesta chave (a de antes
   da onda 296); quem lê e grava é o motor. Conforto, não dado: se o
   navegador recusar (janela anônima, cota), o mapa abre no fundo Mapa. */
const CHAVE_FUNDO = 'labmanager.mapa.fundo';

/* Blumenau, para quando não há pino nenhum: o mapa precisa abrir em
   algum lugar, e a casa fica aqui. [latitude, longitude]. */
const CASA: [number, number] = [-26.9194, -49.0661];

/** O lado do ícone no mapa do Hub, em px (o Portal usa 28). */
const TAMANHO_DO_ICONE = 30;

/** O registro, como o motor o quer: o `Marcador` sem as linhas da API. */
const feicaoDe = (m: Marcador): Feicao => ({
  chave: m.chave, lugar: m.lugar, tipo: m.tipo, estado: m.estado, fonte: m.fonte,
  estimada: m.estimada, nome: m.nome, cliente: m.cliente,
  latitude: m.latitude, longitude: m.longitude,
});

/* ── Os filtros ───────────────────────────────────────────────────── */

/** Os filtros que a URL carrega — uma chave por ficha. */
const CHAVES = ['cliente', 'os', 'obra', 'municipio', 'metodo', 'tipo_ponto',
  'status', 'de', 'ate', 'busca', 'laudo'] as const;
type Chave = typeof CHAVES[number];
type Filtros = Partial<Record<Chave, string>>;

function lerFiltros(p: URLSearchParams): Filtros {
  const f: Filtros = {};
  for (const c of CHAVES) { const v = p.get(c); if (v) f[c] = v; }
  return f;
}

/** Sem acento e sem caixa — «blumenau» acha «Blumenau», «sao» acha «São». */
function chato(s: string | null | undefined): string {
  return (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Uma sugestão da caixa de busca. */
interface Sugestao {
  grupo: string;
  chave: Chave;
  valor: string;
  texto: string;
  detalhe?: string;
}

const GRUPO_ORDEM = ['Clientes', 'Ordens de serviço', 'Obras', 'Cidades',
  'Métodos de ensaio', 'Ensaios de campo'];

/**
 * O que a caixa sugere para o que foi escrito. No máximo seis por grupo:
 * quem tem mais de seis clientes com «pre» escreve mais uma letra.
 */
function sugerir(opcoes: Resposta['opcoes'] | undefined, texto: string): Sugestao[] {
  const q = chato(texto.trim());
  if (!opcoes || q.length < 1) return [];
  const bate = (...campos: (string | null | undefined)[]) =>
    campos.some((c) => chato(c).includes(q));
  const tudo: Sugestao[] = [];
  for (const c of opcoes.clientes) {
    if (bate(c.nome)) tudo.push({ grupo: 'Clientes', chave: 'cliente', valor: c.id, texto: c.nome });
  }
  for (const o of opcoes.os) {
    if (bate(o.numero, o.obra_nome, o.cliente)) {
      tudo.push({ grupo: 'Ordens de serviço', chave: 'os', valor: o.numero,
        texto: `OS ${o.numero}`, detalhe: `${o.obra_nome} · ${o.cliente}` });
    }
  }
  for (const o of opcoes.obras) {
    if (bate(o.nome, o.codigo, o.endereco, o.municipio, o.cliente)) {
      const onde = [o.endereco, o.municipio ? `${o.municipio}/${o.uf ?? ''}` : null]
        .filter(Boolean).join(' · ');
      tudo.push({ grupo: 'Obras', chave: 'obra', valor: o.id, texto: o.nome,
        detalhe: [o.cliente, onde].filter(Boolean).join(' · ') });
    }
  }
  for (const c of opcoes.cidades) {
    if (bate(c.municipio, c.uf)) {
      tudo.push({ grupo: 'Cidades', chave: 'municipio', valor: c.chave,
        texto: `${c.municipio}/${c.uf ?? ''}`, detalhe: plural(c.obras, 'obra', 'obras') });
    }
  }
  for (const m of opcoes.metodos) {
    if (bate(m.codigo, m.nome)) {
      tudo.push({ grupo: 'Métodos de ensaio', chave: 'metodo', valor: m.codigo,
        texto: `${m.codigo} · ${m.nome}`, detalhe: plural(m.ensaios, 'ensaio', 'ensaios') });
    }
  }
  for (const t of opcoes.tiposDePonto) {
    const nome = TIPO_PONTO[t.tipo] ?? t.tipo;
    if (bate(nome, t.tipo)) {
      tudo.push({ grupo: 'Ensaios de campo', chave: 'tipo_ponto', valor: t.tipo,
        texto: nome, detalhe: plural(t.pontos, 'ponto', 'pontos') });
    }
  }
  const porGrupo = new Map<string, Sugestao[]>();
  for (const s of tudo) {
    const lista = porGrupo.get(s.grupo) ?? [];
    if (lista.length < 6) lista.push(s);
    porGrupo.set(s.grupo, lista);
  }
  return GRUPO_ORDEM.flatMap((g) => porGrupo.get(g) ?? []);
}

const ROTULO_FICHA: Record<Chave, string> = {
  cliente: 'Cliente', os: 'OS', obra: 'Obra', municipio: 'Cidade',
  metodo: 'Método', tipo_ponto: 'Ensaio de campo', status: 'Situação',
  de: 'De', ate: 'Até', busca: 'Contém', laudo: 'Laudo',
};

const SITUACOES: { valor: string; texto: string }[] = [
  { valor: '', texto: 'Tudo' },
  { valor: 'aprovado', texto: 'Aprovado / executado' },
  { valor: 'aberto', texto: 'Em andamento' },
  { valor: 'cancelado', texto: 'Cancelado' },
];

/** O texto de uma ficha: o nome, e não o id, que é o que a URL guarda. */
function textoDaFicha(chave: Chave, valor: string, opcoes?: Resposta['opcoes']): string {
  switch (chave) {
    case 'cliente': return opcoes?.clientes.find((c) => c.id === valor)?.nome ?? '…';
    case 'obra': return opcoes?.obras.find((o) => o.id === valor)?.nome ?? '…';
    case 'municipio': {
      const c = opcoes?.cidades.find((x) => x.chave === valor);
      return c ? `${c.municipio}/${c.uf ?? ''}` : valor;
    }
    case 'metodo': {
      const m = opcoes?.metodos.find((x) => x.codigo === valor);
      return m ? `${m.codigo} · ${m.nome}` : valor;
    }
    case 'tipo_ponto': return TIPO_PONTO[valor] ?? valor;
    case 'status': return SITUACOES.find((s) => s.valor === valor)?.texto ?? rotulo(valor);
    case 'de': case 'ate': return fmtData(valor);
    case 'busca': return `“${valor}”`;
    case 'laudo': return 'só com laudo emitido';
    default: return valor;
  }
}

/* ── A tela ───────────────────────────────────────────────────────── */

export function MapaDeEnsaios() {
  const [parametros, setParametros] = useSearchParams();
  const filtros = useMemo(() => lerFiltros(parametros), [parametros]);
  const tipos = useMemo(() => lerTipos(parametros), [parametros]);
  const [resp, setResp] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  /* O que o motor avisou que falhou (spec 05 §5.7): o fundo, o satélite,
     o WebGL, o próprio motor. Os registros continuam em todos os casos. */
  const [falhas, setFalhas] = useState<ReadonlySet<Falha>>(() => new Set());
  const [motorAusente, setMotorAusente] = useState(false);
  const [ajustesAbertos, setAjustesAbertos] = useState(false);
  const [cheia, setCheia] = useState(false);
  const [acervo, setAcervo] = useState<RespostaDoAcervo | null>(null);
  /* 228: o acervo aparece por padrão; `acervo=0` o tira. Os filtros que
     só existem no LabManager não se aplicam a ele — então ele sai. */
  const acervoLigado = parametros.get('acervo') !== '0';
  const filtroSoDoLabManager = (['cliente', 'os', 'obra', 'metodo', 'tipo_ponto', 'status', 'laudo'] as const)
    .some((c) => !!filtros[c]);
  const acervoNoMapa = acervoLigado && !filtroSoDoLabManager;

  const raiz = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  /* O motor do mapa (spec 05 §5.1): nasce uma vez, no efeito abaixo. O
     estado faz os efeitos dos dados e da seleção esperarem por ele; a
     referência serve a quem é chamado de fora do ciclo (o 228). */
  const [controle, setControle] = useState<ControleDoMapa | null>(null);
  const controleAtual = useRef<ControleDoMapa | null>(null);
  /* Os registros da tela agora, e os que o motor já recebeu (para não os
     mandar duas vezes quando ele nasce com os dados já na mão). */
  const registrosAtuais = useRef<Marcador[]>([]);
  const registrosNoMotor = useRef<Marcador[] | null>(null);
  /* 228 (02/10): ajustar a posição de uma obra do acervo — o próximo
     clique no mapa vai para o painel, e um pino provisório mostra onde. */
  const escolherNoMapa = useCallback<EscolherNoMapa>((cb) => {
    const c = controleAtual.current;
    c?.marcarProvisorio(null);
    caixa.current?.classList.toggle('escolhendo-posicao', !!cb);
    c?.aoClicarNoMapa(cb ? (p) => { c.marcarProvisorio(p); cb(p); } : null);
  }, []);

  /* Mudar um filtro é mudar a URL; a tela lê a URL. Uma fonte só. */
  const mudar = useCallback((mudancas: Filtros) => {
    const p = new URLSearchParams(parametros);
    for (const [k, v] of Object.entries(mudancas)) {
      if (v) p.set(k, v); else p.delete(k);
    }
    setParametros(p, { replace: false });
    setSelecionado(null);
  }, [parametros, setParametros]);

  /* O tipo liga e desliga na URL; nenhum marcado é «todos». */
  const alternarTipo = useCallback((t: TipoSimbolo | null) => {
    const p = new URLSearchParams(parametros);
    const novo = new Set(t ? tipos : []);
    if (t) { if (novo.has(t)) novo.delete(t); else novo.add(t); }
    const lista = TIPOS_DE_SIMBOLO.filter((x) => novo.has(x));
    if (lista.length) p.set('tipos', lista.join(',')); else p.delete('tipos');
    setParametros(p, { replace: false });
    setSelecionado(null);
  }, [parametros, setParametros, tipos]);

  /* 228: ligar e desligar o acervo também é URL (`acervo=0`). */
  const alternarAcervo = useCallback(() => {
    const p = new URLSearchParams(parametros);
    if (acervoLigado) p.set('acervo', '0'); else p.delete('acervo');
    setParametros(p, { replace: false });
    setSelecionado(null);
  }, [parametros, setParametros, acervoLigado]);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      for (const c of CHAVES) {
        const v = filtros[c];
        if (!v) continue;
        if (c === 'laudo') q.set('so_com_laudo', '1'); else q.set(c, v);
      }
      const qa = new URLSearchParams();
      for (const c of ['busca', 'de', 'ate', 'municipio'] as const) {
        if (filtros[c]) qa.set(c, filtros[c]!);
      }
      const [r, a] = await Promise.all([
        api<Resposta>(`/mapa?${q}`),
        acervoNoMapa ? api<RespostaDoAcervo>(`/acervo/mapa?${qa}`) : Promise.resolve(null),
      ]);
      setResp(r); setAcervo(a);
    } catch (e) { setErro(e); }
  }, [filtros, acervoNoMapa]);

  useEffect(() => { void carregar(); }, [carregar]);

  const todosOsMarcadores = useMemo(() => [
    ...marcadoresDe(resp?.linhas ?? []),
    ...simbolosDoAcervo(acervo?.obras ?? []).map((a): Marcador => ({
      chave: a.chave, lugar: `acervo:${a.obraId}`, tipo: a.tipo, estado: 'exec',
      fonte: 'acervo', estimada: a.estimada, nome: a.nome, cliente: a.cliente,
      latitude: a.latitude, longitude: a.longitude, linhas: [],
    })),
  ], [resp, acervo]);
  const doAcervo = useMemo(() => todosOsMarcadores.filter((m) => m.fonte === 'acervo').length,
    [todosOsMarcadores]);
  const contagem = useMemo(() => {
    const c = new Map<TipoSimbolo, number>();
    for (const m of todosOsMarcadores) c.set(m.tipo, (c.get(m.tipo) ?? 0) + 1);
    return c;
  }, [todosOsMarcadores]);
  const visiveis = useMemo(() => (tipos.size
    ? todosOsMarcadores.filter((m) => tipos.has(m.tipo)) : todosOsMarcadores),
  [todosOsMarcadores, tipos]);
  registrosAtuais.current = visiveis;
  /* A lista ao lado e o painel do lugar seguem o filtro de tipo. */
  const lugares = useMemo(() => agrupar(visiveis.flatMap((m) => m.linhas)), [visiveis]);
  const lugarSelecionado = lugares.find((l) => l.chave === selecionado) ?? null;
  const obraDoAcervo = selecionado?.startsWith('acervo:') ? selecionado.slice(7) : null;

  /*
   * O mapa nasce uma vez. O motor vem SOB DEMANDA (o MapLibre pesa, e só
   * esta tela o usa); enquanto ele chega, os dados já podem ter chegado —
   * entram assim que ele nasce, e as cargas seguintes, pelo efeito dos dados.
   * Sem WebGL, ou com um erro dentro do motor, o próprio motor mostra a lista
   * e avisa por `aoFalhar('webgl')` ou `aoFalhar('motor')`.
   */
  useEffect(() => {
    let vivo = true;
    let criado: ControleDoMapa | null = null;
    let desligarTema: (() => void) | null = null;
    void (async () => {
      const { criarMapa } = await import('../mapaGeotecnico');
      if (!vivo || !caixa.current) return;
      const c = await criarMapa(caixa.current, {
        casa: CASA,
        chaveDoFundo: CHAVE_FUNDO,
        chaveDasCamadas: 'labmanager.mapa.camadas',
        tema: temaEfetivo(),
        tamanhoDoIcone: TAMANHO_DO_ICONE,
        dica: (f) => dicaDo(f),
        textoDaLista: (f) => textoDaLista(f),
      });
      if (!vivo) { c.destruir(); return; }
      criado = c;
      /* O motor pesa ~1 MB e a resposta da API, não: os dados costumam chegar
         ANTES dele. Entram aqui mesmo, antes de o estilo do mapa carregar — o
         motor os guarda e os desenha quando ele ficar pronto (spec 05). É
         esta entrega, com as camadas de dados ainda inexistentes, que o
         283.27 prova (dados antes do estilo, foco 4 da revisão). */
      c.definirRegistros(registrosAtuais.current.map(feicaoDe));
      c.enquadrar();
      registrosNoMotor.current = registrosAtuais.current;
      c.aoEscolher((lugar) => setSelecionado(lugar));
      c.aoFalhar((f, falhou) => setFalhas((antes) => {
        if (antes.has(f) === falhou) return antes;
        const novo = new Set(antes);
        if (falhou) novo.add(f); else novo.delete(f);
        return novo;
      }));
      desligarTema = aoMudarTemaEfetivo((t) => c.definirTema(t));
      controleAtual.current = c;
      setControle(c);
    })().catch((e) => {
      // O pedaço do motor não veio (rede, versão nova publicada no meio):
      // a lista ao lado continua; a tela diz que o mapa não abriu.
      console.error('mapa: o motor não carregou', e);
      if (vivo) setMotorAusente(true);
    });
    return () => {
      vivo = false;
      desligarTema?.();
      criado?.destruir();
      controleAtual.current = null;
      registrosNoMotor.current = null;
      setControle(null);
    };
  }, []);

  /*
   * Os símbolos: refeitos quando os DADOS mudam, não quando a seleção muda
   * — refazer fecharia o círculo que a pessoa acabou de abrir. Depois,
   * enquadrar o que há (sem símbolo, a casa; um lugar só, zoom de bairro —
   * as regras moram no motor).
   */
  useEffect(() => {
    if (!controle || registrosNoMotor.current === visiveis) return;
    controle.definirRegistros(visiveis.map(feicaoDe));
    controle.enquadrar();
    registrosNoMotor.current = visiveis;
  }, [controle, visiveis]);

  /* A seleção (o halo) — e, pela lista ao lado, o mapa vai até o lugar. */
  useEffect(() => {
    controle?.selecionar(selecionado);
  }, [controle, selecionado]);

  /*
   * A tela cheia (215). A de verdade (Fullscreen API: some a barra do
   * navegador) quando o navegador deixa; senão, a tela inteira da janela
   * por CSS. Esc sai nas duas. O mapa mede a caixa ao nascer — depois de
   * mudar o tamanho, `redimensionar()`.
   */
  const alternarCheia = useCallback(async () => {
    const el = raiz.current;
    if (!el) return;
    if (document.fullscreenElement) { await document.exitFullscreen().catch(() => {}); return; }
    if (cheia) { setCheia(false); return; }
    if (typeof el.requestFullscreen === 'function') {
      try { await el.requestFullscreen(); return; } catch { /* cai no CSS */ }
    }
    setCheia(true);
  }, [cheia]);
  useEffect(() => {
    const mudou = () => setCheia(!!raiz.current && document.fullscreenElement === raiz.current);
    document.addEventListener('fullscreenchange', mudou);
    return () => document.removeEventListener('fullscreenchange', mudou);
  }, []);
  useEffect(() => {
    if (!cheia) return undefined;
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) setCheia(false);
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [cheia]);
  useEffect(() => {
    const t = setTimeout(() => controle?.redimensionar(), 80);
    return () => clearTimeout(t);
  }, [cheia, controle]);

  const opcoes = resp?.opcoes;
  const fichas = CHAVES.filter((c) => filtros[c]);
  const ajustes = ['de', 'ate', 'status', 'laudo'] as const;
  const ajustesEmUso = ajustes.filter((c) => filtros[c]).length;

  return (
    <div className={`mapa-ensaios${cheia ? ' tela-cheia' : ''}`} ref={raiz}>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Mapa Geotécnico</h1>
          <p className="sub">
            O que já se ensaiou por aqui — coleta, furo ou obra — e o laudo de cada um
          </p>
        </div>
        <button type="button" className="botao discreto mapa-tela-cheia"
          data-icone={cheia ? '⤡' : '⛶'} aria-pressed={cheia}
          onClick={() => { void alternarCheia(); }}>
          {cheia ? 'Sair da tela cheia' : 'Tela cheia'}
        </button>
      </div>

      <PainelDoMapa />

      <div className="mapa-busca-bloco">
        <CaixaDeBusca opcoes={opcoes} aoEscolher={(s) => mudar({ [s.chave]: s.valor })}
          aoProcurar={(texto) => mudar({ busca: texto })} />

        <div className="mapa-fichas" aria-live="polite">
          {fichas.length === 0
            ? <span className="sub">Nenhum filtro — o mapa mostra tudo o que tem coordenada.</span>
            : fichas.map((c) => (
              <span key={c} className={`ficha-filtro ${c}`}>
                <small>{ROTULO_FICHA[c]}</small>
                <b>{textoDaFicha(c, filtros[c]!, opcoes)}</b>
                <button type="button" aria-label={`Tirar o filtro ${ROTULO_FICHA[c]}`}
                  onClick={() => mudar({ [c]: '' })}>×</button>
              </span>
            ))}
          {fichas.length > 1 && (
            <button type="button" className="como-link limpar-fichas"
              onClick={() => { setParametros(new URLSearchParams()); setSelecionado(null); }}>
              limpar tudo
            </button>
          )}
        </div>

        <button type="button" className="botao discreto mapa-ajustar"
          aria-expanded={ajustesAbertos} aria-controls="mapa-ajustes"
          onClick={() => setAjustesAbertos((v) => !v)}>
          Ajustar{ajustesEmUso ? ` (${ajustesEmUso})` : ''} {ajustesAbertos ? '▴' : '▾'}
        </button>
        <div className={`mapa-ajustes ${ajustesAbertos ? 'aberto' : ''}`} id="mapa-ajustes">
          <div className="ajuste">
            <span className="rotulo">Período</span>
            <span className="periodo">
              <input type="date" aria-label="De" value={filtros.de ?? ''}
                onChange={(e) => mudar({ de: e.target.value })} />
              <span aria-hidden="true">–</span>
              <input type="date" aria-label="Até" value={filtros.ate ?? ''}
                onChange={(e) => mudar({ ate: e.target.value })} />
            </span>
          </div>
          <div className="ajuste">
            <span className="rotulo">Situação</span>
            <span className="seg" role="group" aria-label="Situação">
              {SITUACOES.map((s) => (
                <button key={s.valor} type="button"
                  aria-pressed={(filtros.status ?? '') === s.valor}
                  onClick={() => mudar({ status: s.valor })}>
                  {s.texto}
                </button>
              ))}
            </span>
          </div>
          <label className="ajuste em-linha">
            <input type="checkbox" checked={!!filtros.laudo}
              onChange={(e) => mudar({ laudo: e.target.checked ? '1' : '' })} />
            <span>Só com laudo emitido</span>
          </label>
        </div>
      </div>

      <div className="mapa-acervo-barra">
        <button type="button" className="chip-acervo" aria-pressed={acervoLigado}
          title={acervoLigado ? 'Tirar do mapa as obras antigas' : 'Mostrar as obras antigas'}
          onClick={alternarAcervo}>
          <span className="amostra-cor" aria-hidden="true" />
          <span>Acervo — antes do LabManager</span>
          {acervoNoMapa && acervo && <small>{acervo.total}</small>}
        </button>
        {acervoLigado && filtroSoDoLabManager && (
          <span className="sub">
            O acervo não entra neste filtro (cliente do cadastro, OS, método,
            situação ou laudo). Procure por escrito — nome, rua, cidade.
          </span>
        )}
      </div>

      <FiltroDeTipos tipos={tipos} contagem={contagem} aoAlternar={alternarTipo} />

      {erro ? <ErroDaApi erro={erro} /> : null}

      {falhas.has('webgl') && (
        <Aviso tipo="atencao" titulo="O mapa não abriu">
          Este navegador não desenha o mapa (WebGL desligado). Os registros estão na lista abaixo.
        </Aviso>
      )}
      {falhas.has('motor') && (
        <Aviso tipo="atencao" titulo="O mapa não abriu">
          Um erro dentro do mapa impediu o desenho nesta página. Os registros estão na lista abaixo.
        </Aviso>
      )}
      {motorAusente && (
        <Aviso tipo="atencao" titulo="O mapa não abriu">
          O mapa não carregou nesta página. Os registros continuam na lista ao lado; recarregue para tentar de novo.
        </Aviso>
      )}
      {/* Um aviso por fonte do fundo: no «Satélite com ruas» as duas podem
          faltar, e trocar de fundo tira o aviso da que saiu (o motor zera). */}
      {falhas.has('fundo') && (
        <Aviso tipo="info" titulo="O fundo do mapa não carregou">
          Os símbolos estão no lugar; o que falta é o mapa de ruas. Sem ele o
          mapa fica liso.
        </Aviso>
      )}
      {falhas.has('satelite') && (
        <Aviso tipo="info" titulo="A imagem de satélite não carregou">
          Os símbolos estão no lugar; o que falta é a imagem de satélite. Sem
          ela o mapa fica liso.
        </Aviso>
      )}

      <p className="sub mapa-resumo" aria-live="polite">
        {!resp ? 'Carregando…' : (
          <>
            <b>{plural(visiveis.length, 'símbolo', 'símbolos')}</b>
            {tipos.size > 0 && <> de {todosOsMarcadores.length}</>}
            {' '}em <b>{plural(lugares.length, 'lugar', 'lugares')}</b>
            {' '}· {plural(resp.total, 'registro', 'registros')}
            {resp.semCoordenada > 0 && (
              <> · <b>{resp.semCoordenada}</b> sem localização (nem a obra tem coordenada)</>
            )}
            {doAcervo > 0 && <> · <b>{doAcervo}</b> do acervo</>}
            {acervo && acervo.semCoordenada > 0 && (
              <> · <b>{acervo.semCoordenada}</b> {acervo.semCoordenada === 1 ? 'obra' : 'obras'} do acervo sem localização</>
            )}
          </>
        )}
      </p>

      <div className="mapa-quadro">
        <div className="mapa-caixa" ref={caixa} role="application"
          aria-label="Mapa Geotécnico" />
        <aside className="mapa-lateral">
          {!resp ? <Carregando /> : obraDoAcervo
            ? <ObraDoAcervo id={obraDoAcervo} aoFechar={() => setSelecionado(null)}
                escolherNoMapa={escolherNoMapa} aoMudarPosicao={() => { void carregar(); }} />
            : lugarSelecionado
            ? <Lugar lugar={lugarSelecionado} aoFechar={() => setSelecionado(null)} />
            : !lugares.length && doAcervo > 0
              ? (
                <p className="sub dica-acervo">
                  Os símbolos azuis são obras do acervo, anteriores ao LabManager.
                  Toque num deles para ver os furos, os ensaios e os relatórios.
                </p>
              )
              : <ListaDeLugares lugares={lugares} aoEscolher={setSelecionado} />}
        </aside>
      </div>

      <p className="sub legenda-mapa">
        <span><span className="pino-mapa simbolo legenda"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'prog') }} /> vazado — programada ou prevista</span>
        <span><span className="pino-mapa simbolo legenda"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'exec') }} /> cheio — executada ou coletada</span>
        <span><span className="pino-mapa simbolo legenda estimada"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'exec') }} /> transparente — só a obra, aproximado</span>
        <span><span className="mapa-cluster p legenda"><b>3</b></span> registros próximos — clique para abrir</span>
        <span><span className="pino-mapa simbolo legenda acervo"
          dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo('sp', 'exec') }} /> azul — acervo, antes do LabManager</span>
      </p>
    </div>
  );
}

/**
 * O filtro por tipo, que é também a legenda (215): os mesmos símbolos do
 * mapa, com a contagem de cada um. Nenhum marcado mostra todos; marcar
 * um mostra só ele; os marcados se somam. Tipo que não aparece nesta
 * busca fica apagado e travado — mas fica, porque a legenda é a mesma
 * em todo mapa.
 */
function FiltroDeTipos({ tipos, contagem, aoAlternar }: {
  tipos: Set<TipoSimbolo>;
  contagem: Map<TipoSimbolo, number>;
  aoAlternar(t: TipoSimbolo | null): void;
}) {
  return (
    <div className="mapa-tipos" role="group" aria-label="Tipos no mapa">
      {GRUPOS_DE_SIMBOLO.map((g) => (
        <div key={g.grupo} className="mapa-tipos-grupo">
          <span className="rotulo">{g.grupo}</span>
          {g.tipos.map(({ tipo, nome }) => {
            const n = contagem.get(tipo) ?? 0;
            const marcado = tipos.has(tipo);
            return (
              <button key={tipo} type="button"
                className={`chip-tipo t-${tipo}${tipos.size && !marcado ? ' apagado' : ''}`}
                aria-pressed={marcado} disabled={!n && !marcado}
                title={marcado ? `Tirar ${nome} do filtro` : `Mostrar ${nome}`}
                onClick={() => aoAlternar(tipo)}>
                <span className="simbolo-do-chip"
                  dangerouslySetInnerHTML={{ __html: desenhoDoSimbolo(tipo, 'exec') }} />
                <span>{nome}</span>
                <small>{n}</small>
              </button>
            );
          })}
        </div>
      ))}
      {tipos.size > 0 && (
        <button type="button" className="como-link limpar-tipos" onClick={() => aoAlternar(null)}>
          mostrar todos os tipos
        </button>
      )}
    </div>
  );
}

/**
 * A caixa de busca com sugestões. Um `combobox` de verdade: setas
 * andam pela lista, Enter escolhe (ou procura o texto em tudo quando
 * nada está marcado), Esc fecha. O clique fora fecha também.
 */
function CaixaDeBusca({ opcoes, aoEscolher, aoProcurar }: {
  opcoes: Resposta['opcoes'] | undefined;
  aoEscolher(s: Sugestao): void;
  aoProcurar(texto: string): void;
}) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [aberta, setAberta] = useState(false);
  const [marcada, setMarcada] = useState(-1);
  const raiz = useRef<HTMLDivElement>(null);
  const sugestoes = useMemo(() => sugerir(opcoes, texto), [opcoes, texto]);
  const temTexto = texto.trim().length > 0;

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberta(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const escolher = (s: Sugestao) => {
    aoEscolher(s); setTexto(''); setAberta(false); setMarcada(-1);
  };
  const procurar = () => {
    if (!temTexto) return;
    aoProcurar(texto.trim()); setTexto(''); setAberta(false); setMarcada(-1);
  };

  /* A última linha da lista é sempre «procurar em tudo». */
  const total = sugestoes.length + (temTexto ? 1 : 0);

  const teclas = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setAberta(true); setMarcada((m) => Math.min(m + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setMarcada((m) => Math.max(m - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (marcada >= 0 && marcada < sugestoes.length) escolher(sugestoes[marcada]);
      else procurar();
    } else if (e.key === 'Escape') {
      setAberta(false); setMarcada(-1);
    }
  };

  let grupoAnterior = '';
  return (
    <div className="mapa-busca" ref={raiz}>
      <span className="lupa" aria-hidden="true">⌕</span>
      <input
        type="search"
        role="combobox"
        aria-label="Buscar no mapa"
        aria-expanded={aberta && total > 0}
        aria-controls={`${id}-lista`}
        aria-activedescendant={marcada >= 0 ? `${id}-op-${marcada}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={opcoes ? 'Cliente, OS, obra, endereço, cidade, amostra, furo, laudo…' : 'Carregando…'}
        disabled={!opcoes}
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAberta(true); setMarcada(-1); }}
        onFocus={() => setAberta(true)}
        onKeyDown={teclas}
      />
      {temTexto && (
        <button type="button" className="botao pequeno" onClick={procurar}>Procurar</button>
      )}
      {aberta && total > 0 && (
        <ul className="mapa-sugestoes" role="listbox" id={`${id}-lista`}>
          {sugestoes.map((s, i) => {
            const cabeca = s.grupo !== grupoAnterior ? s.grupo : null;
            grupoAnterior = s.grupo;
            return (
              <li key={`${s.chave}-${s.valor}`} role="option" id={`${id}-op-${i}`}
                aria-selected={marcada === i}
                className={marcada === i ? 'marcada' : ''}
                data-grupo={cabeca ?? undefined}
                onMouseDown={(e) => { e.preventDefault(); escolher(s); }}
                onMouseEnter={() => setMarcada(i)}>
                {cabeca && <span className="grupo-sugestao">{cabeca}</span>}
                <span className="texto-sugestao">
                  <b>{s.texto}</b>
                  {s.detalhe && <small>{s.detalhe}</small>}
                </span>
              </li>
            );
          })}
          {temTexto && (
            <li role="option" id={`${id}-op-${sugestoes.length}`}
              aria-selected={marcada === sugestoes.length}
              className={`procurar-tudo ${marcada === sugestoes.length ? 'marcada' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); procurar(); }}
              onMouseEnter={() => setMarcada(sugestoes.length)}>
              <span className="texto-sugestao">
                <b>Procurar “{texto.trim()}” em tudo</b>
                <small>OS, obra, endereço, cidade, cliente, amostra, furo, método, número do laudo</small>
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ListaDeLugares({ lugares, aoEscolher }: {
  lugares: Lugar[]; aoEscolher(chave: string): void;
}) {
  if (!lugares.length) {
    return (
      <Vazio>
        Nenhum registro com coordenada para estes filtros. Amostra, ponto
        de investigação e obra podem receber coordenada nas fichas delas.
      </Vazio>
    );
  }
  return (
    <>
      <h3 className="mapa-lateral-titulo">Toque num pino, ou escolha aqui</h3>
      <ul className="lista-lugares">
        {lugares.map((l) => {
          const oss = [...new Set(l.linhas.map((x) => x.os_numero))];
          const primeira = l.linhas[0];
          return (
            <li key={l.chave}>
              <button type="button" className="como-link" onClick={() => aoEscolher(l.chave)}>
                <span className={`pino-mapa ${l.fonte} legenda`}><b>{l.linhas.length}</b></span>
                <span className="lugar-texto">
                  <b>{resumoDoLugar(l)}</b>
                  <small>
                    {primeira.obra_nome} · {primeira.cliente} · OS {oss.join(', ')}
                    {primeira.endereco && ` · ${primeira.endereco}`}
                    {primeira.municipio && ` · ${primeira.municipio}/${primeira.uf ?? ''}`}
                  </small>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** O que aconteceu num lugar: furos, amostras e ensaios, com os laudos. */
function Lugar({ lugar, aoFechar }: { lugar: Lugar; aoFechar(): void }) {
  const pontos = lugar.linhas.filter((l) => l.origem === 'ponto');
  const ensaios = lugar.linhas.filter((l) => l.origem === 'ensaio');
  const previstas = lugar.linhas.filter((l) => l.origem === 'prevista');
  const porAmostra = new Map<string, Linha[]>();
  for (const e of ensaios) {
    const k = e.amostra_id ?? e.id;
    porAmostra.set(k, [...(porAmostra.get(k) ?? []), e]);
  }
  const primeira = lugar.linhas[0];

  return (
    <div className="lugar">
      <div className="linha-topo">
        <div className="cresce">
          <h3>{primeira.obra_nome}</h3>
          <p className="sub">
            {primeira.cliente}
            {primeira.endereco && ` · ${primeira.endereco}`}
            {primeira.municipio && ` · ${primeira.municipio}/${primeira.uf ?? ''}`}
          </p>
        </div>
        <button type="button" className="botao discreto" onClick={aoFechar}>
          Todos os lugares
        </button>
      </div>
      <p className="sub">
        {numero(lugar.latitude, 6)}, {numero(lugar.longitude, 6)} · {FONTE[lugar.fonte]}
      </p>
      {!lugar.exata && lugar.fonte !== 'prevista' && (
        <Aviso tipo="atencao" titulo="Localização aproximada">
          Estes registros não têm coordenada própria: o pino é o da obra.
        </Aviso>
      )}
      {lugar.fonte === 'prevista' && (
        <Aviso tipo="info" titulo="Onde se pretende coletar">
          Coordenada de projeto ou estimada (0190): a amostra ainda não foi
          recebida. Quando chegar, herda esta posição se vier sem a sua.
        </Aviso>
      )}

      {previstas.length > 0 && (
        <section>
          <h4>Amostras previstas</h4>
          <ul className="lista-mapa">
            {previstas.map((p) => (
              <li key={p.id}>
                <div className="lista-mapa-cabeca">
                  <b>{p.amostra_identificacao ?? 'sem identificação'}</b>
                  {/* 303: a prevista ainda não tem código; ele nasce na chegada. */}
                  <small className="nome-amostra-codigo">código AM ao chegar</small>
                  {p.ponto_codigo && <Selo tom="neutro">{p.ponto_codigo}</Selo>}
                  <Selo tom="neutro">{p.origem_coordenada ?? 'projeto'}</Selo>
                </div>
                <small>
                  OS <Link to={`/os/${p.os_id}`}>{p.os_numero}</Link>
                  {p.profundidade_inicial != null && (
                    <> · {numero(p.profundidade_inicial, 2)}
                      {p.profundidade_final != null && <>–{numero(p.profundidade_final, 2)}</>} m</>
                  )}
                  {' · '}por chegar
                </small>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pontos.length > 0 && (
        <section>
          <h4>Ensaios de campo</h4>
          <ul className="lista-mapa">
            {pontos.map((p) => (
              <li key={p.id}>
                <div className="lista-mapa-cabeca">
                  <b>{p.ponto_codigo}</b>
                  <Selo tom="neutro">{TIPO_PONTO[p.ponto_tipo ?? ''] ?? p.ponto_tipo}</Selo>
                  <Selo tom={TOM_STATUS[p.status] ?? 'neutro'}>{rotulo(p.status)}</Selo>
                </div>
                <small>
                  OS <Link to={`/os/${p.os_id}`}>{p.os_numero}</Link>
                  {p.profundidade_executada != null && (
                    <> · {numero(p.profundidade_executada, 2)} m perfurados</>
                  )}
                  {p.profundidade_executada == null && p.profundidade_prevista != null && (
                    <> · {numero(p.profundidade_prevista, 2)} m previstos</>
                  )}
                  {p.data && <> · {fmtData(p.data)}</>}
                </small>
              </li>
            ))}
          </ul>
        </section>
      )}

      {porAmostra.size > 0 && (
        <section>
          <h4>Ensaios de laboratório</h4>
          {[...porAmostra.values()].map((lista) => {
            const a = lista[0];
            return (
              <div key={a.amostra_id ?? a.id} className="amostra-no-mapa">
                <div className="lista-mapa-cabeca">
                  {/* 303 (RN-248): o nome do cliente em destaque, o código
                      logo depois. A OS vem na linha de baixo. */}
                  {a.amostra_codigo
                    ? <Link to={`/amostra/${a.amostra_codigo}`}>
                      <NomeDaAmostra emLinha amostra={daApi(a)} />
                    </Link>
                    : <b>{a.amostra_identificacao ?? 'amostra'}</b>}
                </div>
                <small>
                  OS <Link to={`/os/${a.os_id}`}>{a.os_numero}</Link>
                  {a.ponto_codigo && <> · {a.ponto_codigo}</>}
                  {(a.profundidade_inicial || a.profundidade_final) && (
                    <> · {faixaProfundidade(a.profundidade_inicial, a.profundidade_final)}</>
                  )}
                  {a.data_coleta && <> · coleta {fmtData(a.data_coleta)}</>}
                </small>
                <ul className="lista-mapa">
                  {lista.map((e) => (
                    <li key={e.id}>
                      <div className="lista-mapa-cabeca">
                        <Link to={`/ensaio/${e.id}/situacao`}>
                          {e.metodo_codigo} · {e.metodo_nome}
                        </Link>
                        <SeloDoEnsaio status={e.status} />
                      </div>
                      <small>
                        {e.data_aprovacao
                          ? <>aprovado em {fmtData(e.data_aprovacao)}</>
                          : e.data_execucao ? <>executado em {fmtData(e.data_execucao)}</> : null}
                        {e.laudo_numero && (
                          <> · laudo <b>{e.laudo_numero}</b> R{String(e.laudo_revisao ?? 0).padStart(2, '0')}</>
                        )}
                        {e.status === 'aprovado' && (
                          <>
                            {' · '}
                            <LinkProtegido caminho={`/ensaios/${e.id}/laudo.pdf`}
                              nomeQueda={`laudo-${e.amostra_codigo ?? e.id.slice(0, 8)}-${e.metodo_codigo}.pdf`}>
                              abrir o laudo
                            </LinkProtegido>
                          </>
                        )}
                      </small>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
